import Component from '@glimmer/component';
import { variation } from 'ember-launch-darkly';

// Components
import Flex from 'core/components/flex';
import { Type } from 'core/utils/consts/card-summary';
import CoreCardSummary from 'core/components/core/card/summary';
import {
  HdsBadge,
  HdsBadgeTypeValues,
  HdsCopySnippet,
  HdsCopySnippetColorValues,
  HdsIcon,
  HdsIconColorValues,
  HdsTextBody,
  HdsTextColorValues,
  HdsTextSizeValues,
  HdsTextWeightValues,
  HdsTooltipButton,
  HdsTooltipPlacementValues,
} from '@hashicorp/design-system-components/components';

// Helpers
import { or } from 'ember-truth-helpers';
import { array } from '@ember/helper';
import { t } from 'ember-intl';
import optionForSourceState from 'core/helpers/option-for-source-state';

// Utils
import { AlignItem, FlexDirection, FlexGap } from 'core/utils/flex';

// Types
import type { HashicorpCloudIamUserPrincipal } from '@clients/cloud-iam';
import { HashicorpCloudIamUserPrincipalIdentityType } from '@clients/cloud-iam';
import type { HashicorpCloudIdpIsScimEnabledResponse } from '@clients/cloud-idp';

export interface ManageAccessUsersSummaryCardSignature {
  Element: HTMLElement;
  Args: {
    scimEnabled: HashicorpCloudIdpIsScimEnabledResponse['isEnabled'];
    ssoEnabled: boolean;
    userPrincipal: HashicorpCloudIamUserPrincipal;
  };
  Blocks: {
    default: [];
  };
}

export default class ManageAccessUsersSummaryCardComponent extends Component<ManageAccessUsersSummaryCardSignature> {
  get displayScimProvisioningColumn() {
    const { ssoEnabled } = this.args;
    return ssoEnabled && variation('hcp-identity-scim-enabled');
  }

  get isSyncing() {
    const { scimEnabled, userPrincipal } = this.args;
    return scimEnabled && userPrincipal?.scimSynchronized;
  }

  get isCreatedViaSso() {
    const { scimEnabled, userPrincipal } = this.args;
    return scimEnabled && !userPrincipal?.scimSynchronized;
  }

  get isNonSsoUser() {
    const { userPrincipal } = this.args;

    const sSOIdentityTypes: HashicorpCloudIamUserPrincipalIdentityType[] = [
      HashicorpCloudIamUserPrincipalIdentityType.SAMLP,
      HashicorpCloudIamUserPrincipalIdentityType.HASHICORPSSO,
    ];

    if (!userPrincipal.identityType && !userPrincipal.identityTypes?.length) {
      return false;
    }

    const identityTypes =
      userPrincipal?.identityTypes ??
      (userPrincipal?.identityType ? [userPrincipal.identityType] : []);

    // Return true if none of the user's identity types are SSO types
    return !identityTypes.some((type) => sSOIdentityTypes.includes(type));
  }

  get isScimDisabled() {
    const { scimEnabled } = this.args;
    return !scimEnabled;
  }

  <template>
    <CoreCardSummary
      @type={{Type.Horizontal}}
      data-test-user-detail-summary-card
      ...attributes
      as |Card|
    >
      <Card.Pair as |P|>
        <P.Key
          data-test-user-detail-summary-card-key
          data-test-user-detail-summary-card-key-type
        >
          {{t "manage-access.components.users.summary-card.principal-type"}}
        </P.Key>
        <P.Value
          data-test-user-detail-summary-card-value
          data-test-user-detail-summary-card-value-type
        >
          {{#let (optionForSourceState "policy-type" "user") as |option|}}
            <Flex
              @alignItems={{AlignItem.Center}}
              @direction={{FlexDirection.Row}}
              @gap={{FlexGap.Xs}}
            >
              <span>
                {{! @glint-expect-error: fix incoming icon type }}
                <HdsIcon @name={{option.icon}} @isInline={{true}} />
              </span>
              <span>
                {{t option.text}}
              </span>
            </Flex>
          {{/let}}
        </P.Value>
      </Card.Pair>
      <Card.Pair as |P|>
        <P.Key
          data-test-user-detail-summary-card-key
          data-test-user-detail-summary-card-key-email-id
        >
          {{t "manage-access.components.users.summary-card.email-id"}}
        </P.Key>
        <P.Value
          data-test-user-detail-summary-card-value
          data-test-user-detail-summary-card-value-email-id
        >
          <Flex
            @alignItems={{AlignItem.Center}}
            @direction={{FlexDirection.Row}}
            @gap={{FlexGap.Xs}}
          >
            {{#if @userPrincipal.email}}
              <HdsCopySnippet
                @color={{HdsCopySnippetColorValues.Secondary}}
                @textToCopy={{@userPrincipal.email}}
              />
            {{else}}
              —
            {{/if}}
          </Flex>
        </P.Value>
      </Card.Pair>
      <Card.Pair as |P|>
        <P.Key
          data-test-user-detail-summary-card-key
          data-test-user-detail-summary-card-key-auth-method
        >
          {{t "manage-access.components.users.summary-card.auth-method"}}
        </P.Key>
        <P.Value
          data-test-user-detail-summary-card-value
          data-test-user-detail-summary-card-value-auth-method
        >
          <Flex
            @alignItems={{AlignItem.Center}}
            @direction={{FlexDirection.Row}}
            @gap={{FlexGap.Md}}
          >
            {{#let
              (or
                @userPrincipal.identityTypes (array @userPrincipal.identityType)
              )
              as |identityTypes|
            }}
              {{#each identityTypes as |type|}}
                {{#let
                  (optionForSourceState "identity_types" type)
                  as |option|
                }}
                  <Flex
                    @alignItems={{AlignItem.Center}}
                    @direction={{FlexDirection.Row}}
                    @gap={{FlexGap.Xxs}}
                    data-test-user-item-identity-type-badge
                  >
                    <HdsIcon
                      @color={{option.iconColor}}
                      {{! @glint-expect-error: fix incoming icon type }}
                      @name={{option.icon}}
                      @isInline={{true}}
                    />
                    <span>
                      {{t option.text}}
                    </span>
                  </Flex>
                {{/let}}
              {{/each}}
            {{/let}}
          </Flex>
        </P.Value>
      </Card.Pair>
      {{#if this.displayScimProvisioningColumn}}
        <Card.Pair as |P|>
          <P.Key
            data-test-user-detail-summary-card-key
            data-test-user-detail-summary-card-key-scim-provisioning
          >
            {{t
              "manage-access.components.users.summary-card.scim-provisioning.title"
            }}
          </P.Key>
          <P.Value
            data-test-user-detail-summary-card-value
            data-test-user-detail-summary-card-value-scim-provisioning
          >
            {{#if this.isNonSsoUser}}
              <HdsTextBody
                @color={{HdsTextColorValues.Faint}}
                @size={{HdsTextSizeValues.TwoHundred}}
                @weight={{HdsTextWeightValues.Regular}}
                data-test-user-item-scim-provisioning-non-sso-user
              >
                {{t
                  "manage-access.components.users.summary-card.scim-provisioning.non-sso-user"
                }}
              </HdsTextBody>
            {{else if (or this.isSyncing this.isScimDisabled)}}
              <HdsBadge
                @color={{if this.isSyncing "success"}}
                @icon={{if this.isSyncing "check" "x"}}
                @text={{if
                  this.isSyncing
                  (t
                    "manage-access.components.users.summary-card.scim-provisioning.syncing"
                  )
                  (t
                    "manage-access.components.users.summary-card.scim-provisioning.not-enabled"
                  )
                }}
                @type={{HdsBadgeTypeValues.Outlined}}
                data-test-user-item-scim-provisioning-badge
              />
            {{else}}
              <Flex
                @alignItems={{AlignItem.Center}}
                @direction={{FlexDirection.Row}}
                @gap={{FlexGap.Sm}}
              >
                <HdsTextBody
                  @color={{HdsTextColorValues.Faint}}
                  @size={{HdsTextSizeValues.TwoHundred}}
                  @weight={{HdsTextWeightValues.Regular}}
                  data-test-user-item-scim-provisioning-created-via-sso
                >
                  {{t
                    "manage-access.components.users.summary-card.scim-provisioning.created-via-sso"
                  }}
                </HdsTextBody>
                <HdsTooltipButton
                  @text={{t
                    "manage-access.components.users.summary-card.scim-provisioning.created-via-sso-tooltip"
                  }}
                  @placement={{HdsTooltipPlacementValues.Bottom}}
                  aria-label={{t
                    "manage-access.components.users.summary-card.scim-provisioning.created-via-sso-tooltip"
                  }}
                  data-test-user-item-scim-provisioning-created-via-sso-tooltip
                >
                  <HdsIcon
                    @name="alert-circle"
                    @color={{HdsIconColorValues.Faint}}
                    @isInline={{true}}
                  />
                </HdsTooltipButton>
              </Flex>
            {{/if}}
          </P.Value>
        </Card.Pair>
      {{/if}}
      <Card.Pair as |P|>
        <P.Key
          data-test-user-detail-summary-card-key
          data-test-user-detail-summary-card-key-user-id
        >
          {{t "manage-access.components.users.summary-card.user-id"}}
        </P.Key>
        <P.Value
          data-test-user-detail-summary-card-value
          data-test-user-detail-summary-card-value-user-id
        >
          <HdsCopySnippet
            @color={{HdsCopySnippetColorValues.Secondary}}
            @textToCopy={{@userPrincipal.id}}
          />
        </P.Value>
      </Card.Pair>
    </CoreCardSummary>
  </template>
}
